import React, { useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { usePermissions } from '../contexts/PermissionContext';
import { Role } from '../types/permissions';
import api from '../services/api';

interface UserManagementProps {
  resourceType: 'partner' | 'tenant';
  resourceId: string;
  resourceName: string;
  onUserAdded?: () => void;
  onUserRemoved?: () => void;
}

interface User {
  user_id: string;  // Backend returns user_id, not id
  email: string;
  name: string;
  role: Role;
}

interface InviteUserRequest {
  email: string;
  role: Role;
}

export const UserManagement: React.FC<UserManagementProps> = ({
  resourceType,
  resourceId,
  resourceName,
  onUserAdded,
  onUserRemoved
}) => {
  const { hasRoleAccess, getUserRole, isAdmin, hasPermission } = usePermissions();
  const [open, setOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteData, setInviteData] = useState<InviteUserRequest>({
    email: '',
    role: 'viewer'
  });
  const [inviting, setInviting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; userId: string | null }>({
    open: false,
    userId: null
  });
  const canManageUsers = useMemo(() => 
    isAdmin || hasRoleAccess('admin', resourceType, resourceId) || 
    hasPermission('manage_tenant_users', resourceType, resourceId),
    [isAdmin, hasRoleAccess, hasPermission, resourceType, resourceId]
  );
  const userRole = getUserRole(resourceType, resourceId);

  const loadUsers = useCallback(async () => {
    if (!canManageUsers) return;
    
    setLoading(true);
    setError(null);
    try {
      const endpoint = resourceType === 'partner' 
        ? `/auth/partners/${resourceId}/users`
        : `/auth/tenants/${resourceId}/users`;
      
      const response = await api.get(endpoint);
      setUsers(response.data);
    } catch (err) {
      setError('Failed to load users');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  }, [canManageUsers, resourceType, resourceId]);

  const handleInviteUser = async () => {
    if (!inviteData.email || !inviteData.role) return;
    
    setInviting(true);
    setError(null);
    try {
      const endpoint = resourceType === 'partner'
        ? `/auth/partners/${resourceId}/invite`
        : `/auth/tenants/${resourceId}/invite`;
      
      await api.post(endpoint, inviteData);
      setInviteData({ email: '', role: 'viewer' });
      setInviteOpen(false);
      onUserAdded?.();
      loadUsers();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to invite user';
      setError(msg);
      console.error('Error inviting user:', err);
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveUser = (userId: string) => {
    setConfirmDelete({ open: true, userId });
  };

  const confirmRemoveUser = async () => {
    if (!confirmDelete.userId) return;
    
    try {
      const endpoint = resourceType === 'partner'
        ? `/auth/partners/${resourceId}/users/${confirmDelete.userId}`
        : `/auth/tenants/${resourceId}/users/${confirmDelete.userId}`;
      
      await api.delete(endpoint);
      onUserRemoved?.();
      loadUsers();
    } catch (err) {
      setError('Failed to remove user');
      console.error('Error removing user:', err);
    } finally {
      setConfirmDelete({ open: false, userId: null });
    }
  };

  const handleUpdateUserRole = async (userId: string, newRole: Role) => {
    try {
      const endpoint = resourceType === 'partner'
        ? `/auth/partners/${resourceId}/users/${userId}/role`
        : `/auth/tenants/${resourceId}/users/${userId}/role`;
      
      await api.put(endpoint, { role: newRole });
      loadUsers();
    } catch (err) {
      setError('Failed to update user role');
      console.error('Error updating user role:', err);
    }
  };

  React.useEffect(() => {
    if (open) {
      loadUsers();
    }
  }, [open, resourceId, loadUsers]);



  if (!canManageUsers) {
    return (
      <Button
        variant="outlined"
        startIcon={<PersonIcon />}
        size="small"
        disabled
        title={`No user management access. Your role: ${userRole || 'none'}`}
      >
        Manage Users (User Management Required)
      </Button>
    );
  }

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<PersonIcon />}
        onClick={() => setOpen(true)}
        size="small"
      >
        Manage Users
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Manage Users - {resourceName}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setInviteOpen(true)}
            >
              Invite User
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.user_id}>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <FormControl size="small">
                          <Select
                            value={user.role}
                            onChange={(e) => handleUpdateUserRole(user.user_id, e.target.value as Role)}
                          >
                            <MenuItem value="viewer">Viewer</MenuItem>
                            <MenuItem value="operator">Operator</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveUser(user.user_id)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite User to {resourceName}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={inviteData.email}
            onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={inviteData.role}
              onChange={(e) => setInviteData({ ...inviteData, role: e.target.value as Role })}
              label="Role"
            >
              <MenuItem value="viewer">Viewer</MenuItem>
              <MenuItem value="operator">Operator</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)} disabled={inviting}>
            Cancel
          </Button>
          <Button 
            onClick={handleInviteUser} 
            variant="contained" 
            disabled={!inviteData.email || inviting}
          >
            {inviting ? <CircularProgress size={20} /> : 'Invite'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, userId: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm User Removal</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this user? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ open: false, userId: null })}>
            Cancel
          </Button>
          <Button onClick={confirmRemoveUser} variant="contained" color="error">
            Remove User
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}; 